import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import utils from '@utils';
import * as Yup from 'yup';
import { Modal } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import { Tooltip } from '@components/common';
import { ApplicationService, LocationService, JobService } from '@services';

import './NewJobModal.scss';

import EventControl from '../../../images/event-control.png';
import GeneralSecurity from '../../../images/general-security.png';
import TrafficControl from '../../../images/traffic-control.png';

const NewJobModal = ({ show, onHide }) => {
	const [showModal, setShowModal] = useState(show);
	const [form, setForm] = useState({});
	const [step, setStep] = useState(1);
	const [states, setStateList] = useState({});
	const [locations, setLocations] = useState({});
	const [errors, setErrors] = useState([]);
	const [loading, setLoading] = useState(false);

	// Determine platform for proper datetime handling
	const { isDesktopSafari, isIOSDevice } = utils.getBrowserInfo();

	const dateMask = [/[0-1]/, /[0-9]/, '/', /[0-3]/, /[0-9]/, '/', /2/, /0/, /[2-9]/, /[0-9]/];
	const timeMask = [/[0-1]/, /[0-9]/, ':', /[0-5]/, /[0-9]/, ' ', /(A|P)/i, /M/i];

	/* Form validation */
	const formSchema = [];

	formSchema[1] = Yup.object().shape({
		JobEventTypeId : Yup.number().required('Please specify a job type.'),
	});

	formSchema[2] = Yup.object().shape({
		City          : Yup.string().required(),
		StateId       : Yup.number().required().label('State').typeError('State is required'),
		StreetAddress : Yup.string().required().label('Address'),
		ZipCode       : Yup.number().required().label('Zip Code').typeError('Zip Code must be a number.'),
	});

	formSchema[3] = Yup.object().shape({
		StartDate  : Yup.string().required().label('Start Date'),
		StartTime  : Yup.string().required().label('Start Time'),
		EndTime    : Yup.string().required().label('End Time'),
		JobDetails : Yup.string(),
	});

	// Determine minimum date for native date picker
	const formatMinimumDate = (date = new Date()) => {
		const offset = date.getTimezoneOffset();
		const newDate = new Date(date.getTime() - (offset * 60 * 1000));
		// Set minimum date to next day as workaround to avoid API error
		newDate.setDate(newDate.getDate() + 1);
		return newDate.toISOString().split('T')[0];
	};

	const setLocation = (value) => {
		const locationDetails = locations.find((location) => (location.Id === parseInt(value, 10)));

		// Clear fields if Custom is intentially selected
		if (value === '0') {
			setForm({
				...form,
				StreetAddress : '',
				City          : '',
				ZipCode       : '',
				StateId       : '',
				LocationName  : '',
				LocationId    : 0,
				State         : '',
			});
			return;
		}

		if (locationDetails) {
			// Fix null values from API
			Object.keys(locationDetails).forEach((key) => {
				if (locationDetails[key] === null) { locationDetails[key] = ''; }
			});
			const {
				StreetAddress1 : StreetAddress, City, Name: LocationName, StateId, Zipcode : ZipCode,
			} = locationDetails;

			// Determine if state selection exists
			const State = states.find((state) => state.Id === StateId);

			setForm({
				...form,
				StreetAddress,
				City,
				ZipCode,
				StateId,
				LocationName,
				LocationId : value,
				State      : State ? State.Abbreviation : '',
			});
		} else { setForm({ ...form, LocationName : 'Custom', LocationId : 0 }); }
	};

	const sendJobRequest = () => {
		let StartDateTime = new Date(`${form.StartDate} ${form.StartTime}`);
		let EndDateTime = new Date(`${form.StartDate} ${form.EndTime}`);
		// iOS required specific date formatting to parse into a date object
		if (isIOSDevice) {
			StartDateTime = new Date(`${form.StartDate.replace(/-/gi, '/')} ${form.StartTime}`);
			EndDateTime = new Date(`${form.StartDate.replace(/-/gi, '/')} ${form.EndTime}`);
		}

		/*
			If job end date is less than job start date
			this is an overnight job.
			We need to add one day to the endDate
		*/
		if (EndDateTime < StartDateTime) {
			EndDateTime.setDate(EndDateTime.getDate() + 1);
		}

		// Build job request object
		const jobRequest = {
			...form,
			LocationTypeId : 26,
			StartDateTime  : new Date(StartDateTime.getTime() - (StartDateTime.getTimezoneOffset() * 60000)).toISOString().slice(0, -1),
			EndDateTime    : new Date(EndDateTime.getTime() - (EndDateTime.getTimezoneOffset() * 60000)).toISOString().slice(0, -1),
		};

		// Set default values currently required by API
		if (!form.JobDetails) {
			jobRequest.JobDetails = '(No job notes given.)';
		}

		if (!form.LocationName) {
			jobRequest.LocationName = 'Custom';
		}

		// Append note if recurring
		if (form.isRecurring === 'Yes') {
			jobRequest.JobDetails += '\n\n **This is a recurring job**';
		}

		setLoading(true);

		JobService.saveClientJobRequest(jobRequest)
			.then(() => {
				setStep(step + 1);
			}).catch(() => {
				utils.notify({
					type    : 'error',
					message : 'Request could not be sent. Please try again later.',
					title   : 'Error',
				});
			}).finally(() => setLoading(false));
	};

	// Format date to 12hr display
	const to12hr = (time) => {
		const [h, m] = time.split(':');
		return (`${(h % 12) + 12 * (h % 12 === 0)}:${m} ${h >= 12 ? 'PM' : 'AM'}`);
	};

	// Move to next step
	const nextStep = () => {
		if (step === 1) {
			// set a default location if not present before reaching location page
			if (form.LocationId === undefined && locations.length) { setLocation(locations[0].Id); }
		}

		// AbortEarly flag allows reporting of all yup errors
		formSchema[step].validate(form, { abortEarly : false }).then(() => {
			setErrors([]);
			setStep(step + 1);
		}).catch((err) => {
			const errorList = [];
			err.inner.forEach((error) => {
				const { path, message } = error;
				errorList[path] = message;
			});
			setErrors(errorList);
		});
	};

	useEffect(() => {
		// Get States
		ApplicationService.getUSStates()
			.then(({ data }) => {
				setStateList(data);
			});
		// Get client locations
		LocationService.getAll({ minimalData : false })
			.then(({ data }) => {
				setLocations(data);
			});
		setShowModal(show);
	}, []);

	useEffect(() => {
		if (!show) {
			// Reset form state if closed
			setForm({});
			setStep(1);
			setErrors([]);
			setLoading(false);
		}
		setShowModal(show);
	}, [show]);

	return (
		<Modal id="new-job-modal" show={showModal} onHide={onHide}>
			<Modal.Header closeButton>
				<h4 className="text-center text-primary">Request a Job</h4>
			</Modal.Header>
			<Modal.Body>
				<div className="container-fluid text-center">
					<If condition={step === 1}>
						<div className="animated fadeIn text-center">
							<h4 className="text-center">What will the officer be assisting with?</h4>
							<br />
							<div className="row">
								<div className="col-xs-4">
									<label htmlFor="eventType1">
										<img src={GeneralSecurity} alt="General Security" className="security-type-image" />
										<div>General Security</div>
										<input
											className="event-type-radio"
											type="radio"
											id="eventType1"
											name="eventType"
											checked={form.JobEventTypeId === 1}
											onChange={() => setForm({ ...form, JobEventTypeId : 1, JobEventType : 'General Security' })}
										/>
									</label>
								</div>
								<div className="col-xs-4">
									<label htmlFor="eventType2">
										<img src={TrafficControl} alt="Traffic Control" className="security-type-image" />
										<div>Traffic Control</div>
										<input
											className="event-type-radio"
											type="radio"
											id="eventType2"
											name="eventType"
											checked={form.JobEventTypeId === 2}
											onChange={() => setForm({ ...form, JobEventTypeId : 2, JobEventType : 'Traffic Control' })}
										/>
									</label>
								</div>
								<div className="col-xs-4">
									<label htmlFor="eventType3">
										<img src={EventControl} alt="Event Control" className="security-type-image" />
										<div>Event Control</div>
										<input
											className="event-type-radio"
											type="radio"
											id="eventType3"
											name="eventType"
											checked={form.JobEventTypeId === 3}
											onChange={() => setForm({ ...form, JobEventTypeId : 3, JobEventType : 'Event Control' })}
										/>
									</label>
								</div>
							</div>
							<small className="text-danger">{errors.JobEventTypeId}</small>
						</div>
					</If>
					<If condition={step === 2}>
						<div className="animated fadeIn text-left">
							<h4 className="text-center">Where is the job located?</h4>
							<br />
							<div className="row">
								<div className="col-xs-12 form-group">
									<label htmlFor="location" className="control-label">Location</label>
									<select
										id="location"
										name="location"
										className="form-control text-left"
										value={form.LocationId}
										onChange={(e) => setLocation(e.target.value)}
									>
										{locations.map((location) => (
											<option key={location.Id} value={location.Id}>{`${location.Name}`}</option>
										))}
										<option value="0">(custom)</option>
									</select>
									<small className="text-danger">{errors.LocationId}</small>
								</div>
							</div>
							<div className="row">
								<div className="col-xs-12 form-group">
									<label htmlFor="StreetAddress" className="control-label">Address</label>
									<input
										name="StreetAddress"
										id="StreetAddress"
										className="form-control"
										value={form.StreetAddress}
										onChange={(e) => setForm({ ...form, StreetAddress : e.target.value, LocationId : 0 })}
									/>
									<small className="text-danger">{errors.StreetAddress}</small>
								</div>
							</div>

							<div className="row">
								<div className="col-xs-12 form-group">
									<label htmlFor="City" className="control-label">City</label>
									<input
										name="City"
										id="City"
										className="form-control"
										value={form.City}
										onChange={(e) => setForm({ ...form, City : e.target.value, LocationId : 0 })}
									/>
									<small className="text-danger">{errors.City}</small>
								</div>
							</div>

							<div className="row">
								<div className="col-xs-12 col-sm-6 form-group">
									<label htmlFor="StateId" className="control-label">State</label>
									<select
										name="StateId"
										id="StateId"
										className="form-control"
										value={form.StateId}
										onChange={(e) => setForm({
											...form,
											StateId    : e.target.value,
											State      : states.find((state) => state.Id.toString() === e.target.value).Abbreviation,
											LocationId : 0,
										})}
									>
										<option value="">Select...</option>
										{states.map((state) => (
											<option key={state.Id} value={state.Id}>{`${state.Abbreviation} - ${state.Name}`}</option>
										))}
									</select>
									<small className="text-danger">{errors.StateId}</small>
								</div>

								<div className="col-xs-12 col-sm-6 form-group">
									<label htmlFor="ZipCode" className="control-label">Zip</label>
									<input
										name="ZipCode"
										id="ZipCode"
										className="form-control"
										maxLength="5"
										value={form.ZipCode}
										onChange={(e) => setForm({ ...form, ZipCode : e.target.value, LocationId : 0 })}
									/>
									<small className="text-danger">{errors.ZipCode}</small>
								</div>
							</div>
						</div>
					</If>
					<If condition={step === 3}>
						<div className="animated fadeIn">
							<h4 className="text-center">When do you need the officer?</h4>
							<br />
							<div className="row">
								<div className="col-sm-4 col-sm-offset-4">
									<div className="label-tooltip">
										<label htmlFor="StartDate" className="control-label">Start Date</label>
										<Tooltip content="This is the date the job begins.">
											<i
												className="icon-tooltip rkf rkf-question-solid"
											/>
										</Tooltip>
									</div>
									<Choose>
										<When condition={isDesktopSafari}>
											<MaskedInput
												name="StartDate"
												id="StartDate"
												type="text"
												mask={dateMask}
												className="form-control"
												value={form.StartDate}
												onChange={(e) => setForm({ ...form, StartDate : e.target.value })}
												placeholder="mm/dd/yyyy"
											/>
										</When>
										<Otherwise>
											<input
												name="StartDate"
												id="StartDate"
												className="form-control"
												value={form.StartDate}
												onChange={(e) => setForm({ ...form, StartDate : e.target.value })}
												type="date"
												min={formatMinimumDate()}
											/>
										</Otherwise>
									</Choose>
									<small className="text-danger">{errors.StartDate}</small>
								</div>
							</div>
							<div className="row">
								<div className={`col-sm-4 col-sm-offset-1 no-pad-right${isIOSDevice ? ' text-left' : ''}`}>
									<label htmlFor="StartTime" className="control-label">From</label>
									<Choose>
										<When condition={isDesktopSafari}>
											<MaskedInput
												id="StartTime"
												name="StartTime"
												type="text"
												mask={timeMask}
												className="form-control"
												value={form.StartTime}
												onChange={(e) => setForm({ ...form, StartTime : e.target.value })}
												placeholder="__:__ __"
											/>
										</When>
										<Otherwise>
											<input
												name="StartTime"
												id="StartTime"
												className="form-control"
												value={form.StartTime}
												onChange={(e) => setForm({ ...form, StartTime : e.target.value })}
												type="time"
											/>
										</Otherwise>
									</Choose>
									<small className="text-danger">{errors.StartTime}</small>
								</div>
								<div className="col-sm-2 text-gray">
								&nbsp;
								</div>
								<div className={`col-sm-4 no-pad-left${isIOSDevice ? ' text-left' : ''}`}>
									<label htmlFor="jobEndsAt" className="control-label">To</label>
									<Choose>
										<When condition={isDesktopSafari}>
											<MaskedInput
												id="EndTime"
												name="EndTime"
												type="text"
												mask={timeMask}
												className="form-control"
												value={form.EndTime}
												onChange={(e) => setForm({ ...form, EndTime : e.target.value })}
												placeholder="__:__ __"
											/>
										</When>
										<Otherwise>
											<input
												name="EndTime"
												id="EndTime"
												className="form-control"
												value={form.EndTime}
												onChange={(e) => setForm({ ...form, EndTime : e.target.value })}
												type="time"
											/>
										</Otherwise>
									</Choose>
									<small className="text-danger">{errors.EndTime}</small>
								</div>
							</div>
							<div className="row text-left">
								<div className="col-xs-10 col-xs-offset-1 text-center">
									<br />
									<label className="checkbox-inline">
										<input type="checkbox" name="isRecurring" value={form.isRecurring} checked={form.isRecurring === 'Yes'} onChange={(e) => setForm({ ...form, isRecurring : e.target.checked ? 'Yes' : 'No' })} />
										This is a recurring job
									</label>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-10 col-sm-offset-1">
									<br />
									<div className="label-tooltip">
										<label htmlFor="additionalDetails" className="control-label">Additional Job Details</label>
										<Tooltip content="Is there anything else we should know about the job?">
											<i
												className="icon-tooltip rkf rkf-question-solid"
											/>
										</Tooltip>
									</div>
									<textarea
										name="JobDetails"
										id="JobDetails"
										className="form-control"
										placeholder="Optional"
										value={form.JobDetails}
										onChange={(e) => setForm({ ...form, JobDetails : e.target.value })}
									/>
									<small className="text-danger">{errors.JobDetails}</small>
								</div>
							</div>
						</div>
					</If>
					<If condition={step === 4}>
						<div className="animated fadeIn">
							<h4 className="text-center">Confirm Details</h4>
							<br />
							<div className="row">
								<div className="col-sm-12">
									<h5><strong>Location:</strong></h5>
									<p>
										{ form.StreetAddress }
										<br />
										{ `${form.City}, ${form.State} ${form.ZipCode}` }
									</p>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<h5><strong>Time:</strong></h5>
									<p>
										{ new Date(form.StartDate).toLocaleDateString('en-US', { timeZone : 'UTC' }) }
										<br />
										{ `${to12hr(form.StartTime)} - ${to12hr(form.EndTime)}` }
										<br />
										{ form.isRecurring === 'Yes' ? 'Recurring Job? Yes' : '' }
									</p>
								</div>
							</div>
							<div className="row">
								<p>
									You are submitting this job request to be reviewed. You
									will be contacted by an off-duty coordinator within 24 hours
									to confirm the details of your job request.
								</p>
								<br />
								<If condition={step === 4}>
									<div className="col-xs-6 col-xs-offset-3 text-center">
										<button
											type="button"
											className="btn btn-primary btn-block"
											disabled={loading}
											onClick={() => sendJobRequest()}
										>
											{ `${loading ? '' : 'Send Request'}` }
											<If condition={loading}>
												&nbsp;
												<span className="glyphicon glyphicon-refresh rkf-spin" />
											</If>
										</button>
									</div>
								</If>
							</div>
						</div>
					</If>
					<If condition={step === 5}>
						<div className="animated fadeIn">
							<h4 className="text-center">Request Sent!</h4>
							<br />
							<p>
								Your job request has been submitted. You will be contacted by an off-duty
								coordinator	within 24 hours to confirm the details of your job request.
							</p>
						</div>
					</If>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className="row">
					<If condition={step > 1 && step < 5}>
						<div className={`col-xs-4 ${step >= 4 ? 'col-xs-offset-4' : 'col-xs-offset-2'}`}>
							<button
								type="button"
								className="btn btn-default btn-block"
								onClick={() => setStep(step - 1)}
							>
								Back
							</button>
						</div>
					</If>
					<If condition={step < 4}>
						<div className={`col-xs-4 ${step === 1 ? 'col-xs-offset-4' : ''}`}>
							<button
								type="button"
								className="btn btn-primary btn-block"
								onClick={() => nextStep()}
							>
								Next
							</button>
						</div>
					</If>
					<If condition={step === 5}>
						<div className="col-xs-4 col-xs-offset-4">
							<button
								type="button"
								className="btn btn-primary btn-block"
								onClick={() => onHide()}
							>
								OK
							</button>
						</div>
					</If>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

NewJobModal.propTypes = {
	show   : PropTypes.bool,
	onHide : PropTypes.func.isRequired,
};

NewJobModal.defaultProps = {
	show : false,
};

export default NewJobModal;
